import { useAuth0 } from '@auth0/auth0-react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { getStoredAccessToken } from '../utils/auth0Utils';

// Build-time constant — Vite replaces this with the literal value at compile time.
const AUTH_PROVIDER = import.meta.env.VITE_AUTH_PROVIDER || 'auth0';

export interface AuthStateResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Raw IdP user object (shape differs by provider — use only for null-checks) */
  user: unknown;
  /** Returns the correct token for making backend API calls */
  getAccessTokenSilently: () => Promise<string>;
  /** Redirects to the IdP login page */
  signinRedirect: (returnTo?: string) => void;
}

/**
 * Provider-agnostic replacement for `useAuth0()` used in contexts and components
 * that only need basic auth state and the ability to get a token for API calls.
 *
 * VITE_AUTH_PROVIDER is a build-time constant so only one branch executes at runtime.
 * The eslint-disable comments below acknowledge this intentional pattern.
 */
export function useAuthState(): AuthStateResult {
  if (AUTH_PROVIDER === 'authentik') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const auth = useOidcAuth();
    return {
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
      user: auth.user?.profile ?? null,
      // For API calls: use the extended JWT stored by appAuthService (validated by
      // the backend's ExtendedTokenAuthentication class), not the raw Authentik token.
      getAccessTokenSilently: async () => getStoredAccessToken() || '',
      signinRedirect: (returnTo?: string) => {
        auth.signinRedirect({ state: { returnTo } });
      },
    };
  }

  // Auth0 path
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  return {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently: async () => {
      return getAccessTokenSilently();
    },
    signinRedirect: (returnTo?: string) => {
      loginWithRedirect({ appState: { returnTo } });
    },
  };
}
